import { Award, Trophy, BookOpen, Euro, CheckCircle2, ArrowRight, Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import GermanyResourceShell from './_components/GermanyResourceShell';

const DESC = 'Germany scholarships 2026 for Indian students: DAAD, Erasmus+, Deutschlandstipendium, Heinrich Böll & more — eligibility, awards, deadlines compared.';

const SCHOLARSHIPS = [
  { name: 'DAAD Scholarship',
    type: 'Government',
    level: 'Masters / PhD',
    award: '€934 – €1,300/month + tuition + travel + insurance',
    eligibility: 'GPA ≥ 7.5, 2 years work experience for Masters, research-aligned for PhD',
    deadline: 'Oct–Dec (varies by programme)',
    coverage: 'Full funding',
    notes: 'The flagship German government scholarship. Most prestigious — competition is fierce.',
  },
  { name: 'Erasmus+ ICM',
    type: 'EU-funded',
    level: 'Bachelor / Master / Exchange',
    award: '€800 – €1,000/month + travel grant',
    eligibility: 'Enrolled in an Indian university with Erasmus+ partnership',
    deadline: 'Feb–Mar (annual cycle)',
    coverage: 'Partial / exchange semester',
    notes: '3–12 month exchange. Indian university must hold an Erasmus+ ICM agreement.',
  },
  { name: 'Deutschlandstipendium',
    type: 'University + Private',
    level: 'Bachelor / Master',
    award: '€300/month for 2 semesters',
    eligibility: 'Academic excellence + social engagement (CGPA ≥ 8.0)',
    deadline: 'Aug–Oct (per university)',
    coverage: 'Top-up scholarship (combinable)',
    notes: 'Combinable with DAAD/BAföG. Apply directly to your German university.',
  },
  { name: 'Heinrich Böll Foundation',
    type: 'Political Foundation',
    level: 'Master / PhD',
    award: '€934/month + research allowance',
    eligibility: 'Strong commitment to ecology, democracy, gender equity',
    deadline: 'Mar / Sep (twice a year)',
    coverage: 'Full funding',
    notes: 'For students whose work aligns with Green political values. Strong essay focus.',
  },
  { name: 'Konrad-Adenauer-Stiftung',
    type: 'Political Foundation',
    level: 'Master / PhD',
    award: '€934/month + research grant',
    eligibility: 'Christian-democratic values, social engagement, GPA ≥ 8.0',
    deadline: 'Jul / Jan (twice a year)',
    coverage: 'Full funding',
    notes: 'Aligned with CDU values. Strong on policy & political science applicants.',
  },
  { name: 'Friedrich Ebert Stiftung',
    type: 'Political Foundation',
    level: 'Master / PhD',
    award: '€934/month + travel + family',
    eligibility: 'Social-democratic values, financial need, GPA ≥ 7.5',
    deadline: 'Aug / Feb (twice a year)',
    coverage: 'Full funding',
    notes: 'SPD-aligned. Heavy preference for students from developing nations including India.',
  },
  { name: 'Bayer Foundation Fellowship',
    type: 'Corporate',
    level: 'Master / PhD',
    award: '€10,000 – €15,000 lumpsum',
    eligibility: 'STEM students, project alignment with Bayer R&D',
    deadline: 'Sep–Oct',
    coverage: 'Project-based grant',
    notes: 'Best for life sciences, chemistry, pharma & ag-tech students.',
  },
];

const TIPS = [
  { tip: 'Apply 12–14 months before intake',           why: 'DAAD deadlines are typically Oct–Dec for the NEXT-year October intake.' },
  { tip: 'Combine 1 government + 1 university award',  why: 'Deutschlandstipendium is stackable with DAAD or BAföG — many students win both.' },
  { tip: 'Tailor essays to foundation values',         why: 'Political foundations weight value-alignment essays heavier than GPA.' },
  { tip: 'Get 2 academic + 1 professional recommender',why: 'Most German scholarships require this exact combination — start asking 6 weeks in advance.' },
  { tip: 'IELTS 6.5 OR German B2 (whichever applies)', why: 'Language proficiency below this auto-rejects ~25% of Indian applicants.' },
];

const FAQS = [
  { q: 'What is the best scholarship to study in Germany for Indian students?',
    a: 'DAAD (Deutscher Akademischer Austauschdienst) is the gold standard — it offers full tuition + €934-€1,300/month + travel grant + insurance for the entire programme duration. However, DAAD is highly competitive. If you have strong work experience, apply for DAAD; if you are an undergrad, target Deutschlandstipendium (€300/month, top-up grant) or the political foundation scholarships (Böll, Adenauer, Ebert).' },
  { q: 'Is studying in Germany free for Indian students?',
    a: 'Tuition is free at most public German universities (only Baden-Württemberg charges €1,500/sem for non-EU students). However, "free" tuition does NOT cover the €11,904 blocked account requirement for living expenses. A full scholarship like DAAD covers BOTH tuition and living costs — effectively making your Germany degree completely free.' },
  { q: 'When should I apply for German scholarships?',
    a: 'DAAD deadlines are typically October–December for the following year\'s October intake (so 12–14 months in advance). Erasmus+ runs in Feb–Mar. Political foundations have two cycles per year. Apply BEFORE you have a confirmed German university admission — scholarship bodies care more about your profile and intent than your exact university.' },
  { q: 'Can I work part-time AND hold a scholarship?',
    a: 'Yes — most German scholarships allow you to work up to 20 hours/week during the semester (the same as the German student visa rule). However, DAAD and political foundation scholarships expect you to focus on studies and may reduce the stipend if work earnings exceed €450/month consistently.' },
  { q: 'Are there scholarships specifically for women from India?',
    a: 'Yes. The DAAD-Women in STEM scholarship, the Heinrich Böll Foundation Gender Equity track, and the BAYER Foundation Women in Pharma fellowship all have dedicated women-focused tracks. Additionally, many private universities (e.g. IU, GISMA) offer 5–25% tuition discounts for Indian female applicants in STEM.' },
  { q: 'How competitive are these scholarships?',
    a: 'DAAD: ~7% acceptance rate (very competitive). Deutschlandstipendium: ~15% (moderately competitive). Political foundations: ~10–12%. Erasmus+ ICM: depends on your home university. Tip — apply to 3–4 scholarships in parallel rather than betting everything on DAAD.' },
];

const GermanyScholarshipsPage = () => (
  <GermanyResourceShell
    slug="scholarships"
    breadcrumbLabel="Scholarships"
    articleHeadline="Germany Scholarships 2026 — Complete Guide for Indian Students"
    articleDescription={DESC}
    faqs={FAQS}
    related={['cost-of-study', 'public-universities', 'student-visa']}
  >
    {/* Hero */}
    <section className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #003087 0%, #001F5C 60%, #020617 100%)' }}>
      <div className="relative max-w-7xl mx-auto px-6 py-16 lg:py-20">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full px-4 py-1.5 mb-5">
            <Award className="w-3.5 h-3.5 text-[#C5A028]" />
            <span className="text-xs text-white/90 font-medium">Germany Resource — Scholarships</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold font-syne text-white leading-tight mb-4">
            Germany Scholarships 2026 — Complete Funding Guide for Indians
          </h1>
          <p className="text-base lg:text-lg text-slate-300 leading-relaxed mb-6">
            Germany offers 100+ scholarships to international students — but only 7 consistently fund Indian applicants well. This is the complete 2026 funding playbook: DAAD vs Erasmus+ vs Deutschlandstipendium vs political foundations, what they fund, who they fund, and the application timeline that wins.
          </p>
          <div className="grid grid-cols-3 gap-3 max-w-xl">
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Best Award</div>
              <div className="text-base font-bold text-[#C5A028]">€934–1,300/mo</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Apply</div>
              <div className="text-base font-bold text-[#C5A028]">12 mo ahead</div>
            </div>
            <div className="bg-white/5 border border-white/10 rounded-xl p-3">
              <div className="text-[10px] text-slate-400 uppercase tracking-wider mb-0.5">Stackable</div>
              <div className="text-base font-bold text-[#C5A028]">Yes (top-ups)</div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* 7 Major scholarships */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-6xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-2">7 Best Scholarships for Indian Students</h2>
        <p className="text-sm text-slate-600 mb-6">Selected on award size, Indian acceptance rate, and reliability. Listed by relevance, not order.</p>
        <div className="space-y-4">
          {SCHOLARSHIPS.map((s) => (
            <div key={s.name} className="bg-slate-50 border border-slate-200 rounded-xl p-5 hover:border-[#003087] transition-all">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <h3 className="text-lg font-bold font-syne text-[#003087]">{s.name}</h3>
                  <div className="flex flex-wrap gap-2 mt-1.5">
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#003087]/10 text-[#003087] px-2 py-0.5 rounded-full">{s.type}</span>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">{s.level}</span>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[10px] text-slate-500 uppercase tracking-wider">Coverage</div>
                  <div className="text-sm font-bold text-slate-900">{s.coverage}</div>
                </div>
              </div>
              <div className="grid md:grid-cols-3 gap-3 text-sm">
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Award</div>
                  <div className="text-slate-800 font-medium">{s.award}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Eligibility</div>
                  <div className="text-slate-800">{s.eligibility}</div>
                </div>
                <div>
                  <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-0.5">Deadline</div>
                  <div className="text-slate-800 font-medium">{s.deadline}</div>
                </div>
              </div>
              <p className="text-xs text-slate-600 mt-3 italic">{s.notes}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Application tips */}
    <section className="py-14 lg:py-18 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-5">5 Application Tips That Actually Move the Needle</h2>
        <div className="space-y-3">
          {TIPS.map((t, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-5 flex gap-4">
              <div className="w-10 h-10 rounded-full bg-[#003087] text-white font-bold flex items-center justify-center shrink-0">{i + 1}</div>
              <div className="flex-1">
                <h3 className="text-base font-bold font-syne text-slate-900 mb-1.5">{t.tip}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{t.why}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* FAQ */}
    <section className="py-14 lg:py-18 bg-white">
      <div className="max-w-4xl mx-auto px-6">
        <h2 className="text-2xl lg:text-3xl font-bold font-syne text-[#003087] mb-7">Frequently Asked Questions</h2>
        <div className="space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="group bg-slate-50 border border-slate-200 rounded-xl p-5">
              <summary className="font-syne font-semibold text-slate-900 cursor-pointer text-[15px] flex items-start justify-between">
                <span className="pr-6">{f.q}</span>
                <span className="text-[#003087] group-open:rotate-45 transition-transform text-xl leading-none">+</span>
              </summary>
              <p className="mt-3 text-sm leading-7 text-slate-700">{f.a}</p>
            </details>
          ))}
        </div>
      </div>
    </section>
  </GermanyResourceShell>
);

export default GermanyScholarshipsPage;
